import { Subheading } from '@/src/tailwindui/heading';
import { Button } from '@headlessui/react';
import { ExternalLinkIcon } from '@radix-ui/react-icons';
import useGalleryStats from './hooks/useGalleryStats';
import emptyState from '../../image-empty-state.png';
import SmallLoader from '@/src/Common/components/SmallLoader';
import { cn } from '@/src/lib/utils';
import FeatureFlag from '@/src/Features/components/FeatureFlag';
import { FeatureFlags } from '@/src/Features/lib/constants';
import useGalleryUrl from './hooks/useGalleryUrl';
import { useState, useEffect } from 'react';
export default function GalleryStats({
    activationId,
}: {
    activationId: number;
}) {
    const [galleryUrl, setGalleryUrl] = useState<string | null>(null);
    const {
        totalPhotos,
        lastPhotoTaken,
        isLoading,
        slug,
        galleryId,
        moderationToken,
    } = useGalleryStats(activationId);

    const { getOrgDomain } = useGalleryUrl();

    useEffect(() => {
        const fetchGalleryUrl = async () => {
            const url = await getOrgDomain();
            setGalleryUrl(url);
        };
        fetchGalleryUrl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="mx-auto max-w-4xl w-full overflow">
            {lastPhotoTaken ? (
                <div
                    className={`flex relative flex-col gap-4 h-[200px] w-[100%] bg-cover bg-top`}
                    style={{
                        backgroundImage: `url(${lastPhotoTaken})`,
                    }}
                >
                    <div className="fade-to-background  absolute bottom-0 w-[calc(100%-2px)] h-[50px] z-10 ml-[1px] pr-[1px]"></div>
                </div>
            ) : (
                <div className="flex relative flex-col gap-4 h-[200px] w-[100%] bg-cover bg-top">
                    <img
                        alt="Empty State"
                        className="w-full h-full object-cover"
                        src={emptyState}
                    />
                    <div className="fade-to-background  absolute bottom-0 w-[calc(100%-2px)] h-[50px] z-10 ml-[1px] pr-[1px]"></div>
                </div>
            )}
            <div className="flex flex-wrap items-end justify-between gap-4 transition-all duration-300 px-4 ">
                <div className="flex flex-row justify-between w-full">
                    <div className="flex flex-col gap-2 pt-4 w-full">
                        <Subheading>Total Photos</Subheading>
                        <div className="flex items-center gap-2 w-full">
                            {totalPhotos === 0 ? (
                                <p className="text-md text-[#262C2C] dark:text-white">
                                    There haven't been any photos taken just
                                    yet.
                                </p>
                            ) : (
                                <p className="text-4xl monospace-font text-[#262C2C] dark:text-white">
                                    {totalPhotos}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <FeatureFlag flag={FeatureFlags.PE_GALLERY_MODERATION}>
                <div className="absolute bottom-6 right-6">
                    <Button
                        className="light-button flex text-white px-4 py-2 rounded-md"
                        onClick={() => {
                            if (moderationToken) {
                                window.open(
                                    `${galleryUrl}/moderation/${
                                        slug ?? galleryId
                                    }/${moderationToken}`,
                                    '_blank',
                                );
                            } else {
                                console.log('no token');
                            }
                        }}
                    >
                        Manage&nbsp;&nbsp;&rarr;
                    </Button>
                </div>
            </FeatureFlag>
            <div className="absolute top-4 right-4">
                <Button
                    className="light-button-no-transparency flex text-white px-4 py-2 rounded-md justify-center items-center gap-2 mb-2"
                    onClick={() => {
                        window.open(
                            `${galleryUrl}/gallery/${slug ?? galleryId}`,
                            '_blank',
                        );
                    }}
                >
                    Gallery &nbsp; <ExternalLinkIcon className="w-4 h-4" />
                </Button>
            </div>
            <div className="my-10 mt-6" />
            <div
                className={cn(
                    'flex justify-center items-center h-full w-full absolute top-0 left-0 bg-white dark:bg-black transition-all duration-300',
                    isLoading ? 'opacity-100' : 'opacity-0 invisible',
                )}
            >
                <SmallLoader />
            </div>
        </div>
    );
}
